'use client'

import { useAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { type HomeAiBriefState, useHomeAiBrief } from '@/features/home/hooks/use-home-ai-brief'
import type { HomeSnapshot } from '@/features/home/hooks/use-home-snapshot'
import { useLiveTickers } from '@/features/home/hooks/use-live-tickers'
import { buildBriefPrompt } from '@/features/home/lib/home-brief-prompt'
import {
  type Datapoint,
  type DatapointKind,
  selectDatapoints,
} from '@/features/home/lib/home-datapoints'
import {
  type AssetExposure,
  buildExposure,
  fundingAprPercent,
} from '@/features/home/lib/home-exposure'
import { buildIdeas, describeIdea, type TradeIdea } from '@/features/home/lib/home-ideas'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { homeHiddenDatapointsAtom } from '@/state/home'
import type { Exchange, Instrument, Ticker } from '@/types/generated/common/types'

export interface HomeDailyBrief {
  /** False until the book has loaded and settled; the brief shows placeholders until then. */
  ready: boolean
  exposures: AssetExposure[]
  datapoints: Datapoint[]
  ideas: TradeIdea[]
  tickerOf: (symbol: string | undefined) => Ticker | undefined
  instrumentOf: (
    instrument: { symbol: string; exchange: Exchange } | undefined
  ) => Instrument | undefined
  /** Cards report the line they'd say about their number (see home-datapoint-cards.tsx). */
  reportSummary: (id: string, line: string | null) => void
  ai: HomeAiBriefState
  /** Ask the AI to write the brief over what's on screen now. */
  writeBrief: () => void
  /** The whole AI answer, in a dialog ("Read the full brief"). */
  fullBriefOpen: boolean
  setFullBriefOpen: (open: boolean) => void
  /** "Not useful": stop picking this kind of datapoint; and undo for all. */
  hideKind: (kind: DatapointKind) => void
  hiddenCount: number
  showAllKinds: () => void
}

/** How long the book must stay loaded before the picks are made (other accounts land late). */
const SETTLE_MS = 1200
/** Never hold the placeholders longer than this. */
const MAX_WAIT_MS = 8000

/** True once `condition` has held for `settleMs` (or `maxMs` has passed); then stays true. */
function useSettled(condition: boolean, settleMs: number, maxMs: number): boolean {
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    if (settled) return
    const timer = setTimeout(() => setSettled(true), maxMs)
    return () => clearTimeout(timer)
  }, [settled, maxMs])
  useEffect(() => {
    if (settled || !condition) return
    const timer = setTimeout(() => setSettled(true), settleMs)
    return () => clearTimeout(timer)
  }, [settled, condition, settleMs])
  return settled
}

/**
 * The daily brief behind Home's "Today's brief" and "Ideas for you": the book grouped by asset,
 * the datapoints picked for it, the ideas for it, the live tickers they read, and the AI run that
 * comments on all of it.
 */
export function useHomeDailyBrief(
  snapshot: HomeSnapshot,
  openOrderSymbols: string[]
): HomeDailyBrief {
  const exposures = useMemo(
    () => buildExposure(snapshot.risk.items, openOrderSymbols),
    [snapshot.risk.items, openOrderSymbols]
  )
  const [hiddenKinds, setHiddenKinds] = useAtom(homeHiddenDatapointsAtom)
  const liveDatapoints = useMemo(
    () => selectDatapoints(exposures, new Set(hiddenKinds as DatapointKind[])),
    [exposures, hiddenKinds]
  )

  // Before the first positions snapshot the book looks empty, and the brief would pick the
  // watchlist and then swap every card when the positions land. Wait until it has settled.
  const { portfolio } = snapshot
  const ready = useSettled(
    !portfolio.isAccountsLoading && (portfolio.accountCount === 0 || !snapshot.isPositionsLoading),
    SETTLE_MS,
    MAX_WAIT_MS
  )
  const instrumentMap = useMultiExchangeInstrumentMap()

  const watched = useMemo(() => {
    const seen = new Map<string, { symbol: string; exchange: Exchange }>()
    for (const datapoint of liveDatapoints) {
      if (datapoint.perp) seen.set(datapoint.perp.symbol, datapoint.perp)
    }
    for (const exposure of exposures) {
      for (const perp of exposure.perps) seen.set(perp.symbol, perp)
    }
    return [...seen.values()]
  }, [liveDatapoints, exposures])
  const tickers = useLiveTickers(watched)

  const instrumentOf = useCallback(
    (instrument: { symbol: string; exchange: Exchange } | undefined) =>
      instrument
        ? getInstrumentMapForExchange(instrumentMap, instrument.exchange).get(instrument.symbol)
        : undefined,
    [instrumentMap]
  )

  const fundingApr = useMemo(() => {
    const apr: Record<string, number> = {}
    for (const perp of watched) {
      const rate = Number(tickers.get(perp.symbol)?.fundingRate)
      if (Number.isFinite(rate) && tickers.get(perp.symbol)?.fundingRate) {
        apr[perp.symbol] = fundingAprPercent(rate, instrumentOf(perp)?.fundingInterval)
      }
    }
    return apr
  }, [watched, tickers, instrumentOf])

  const liveIdeas = useMemo(
    () => buildIdeas({ risks: snapshot.risk.items, exposures, fundingApr }),
    [snapshot.risk.items, exposures, fundingApr]
  )

  // Picks stay put while the book is the same: marks moving a position across a threshold (or
  // two holdings swapping size) must not reshuffle the cards under the user. A new or closed
  // position, or a hidden kind, picks again. Ideas keep their slots but show live numbers.
  const signature = useMemo(
    () =>
      [
        [...new Set(snapshot.risk.items.map((risk) => risk.key))].sort().join('|'),
        hiddenKinds.join(','),
      ].join('#'),
    [snapshot.risk.items, hiddenKinds]
  )
  const frozenRef = useRef<{
    signature: string
    datapoints: Datapoint[]
    ideas: TradeIdea[]
  } | null>(null)
  if (ready && frozenRef.current?.signature !== signature) {
    frozenRef.current = { signature, datapoints: liveDatapoints, ideas: liveIdeas }
  }
  const datapoints = useMemo(
    () => (ready && frozenRef.current ? frozenRef.current.datapoints : []),
    // frozenRef changes together with `signature`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, signature]
  )
  const ideas = useMemo(
    () =>
      ready && frozenRef.current
        ? frozenRef.current.ideas.map(
            (frozen) => liveIdeas.find((idea) => idea.id === frozen.id) ?? frozen
          )
        : [],
    // frozenRef changes together with `signature`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, signature, liveIdeas]
  )

  const [summaries, setSummaries] = useState<Record<string, string>>({})
  const [fullBriefOpen, setFullBriefOpen] = useState(false)
  const reportSummary = useCallback((id: string, line: string | null) => {
    setSummaries((current) => {
      if (line === null) {
        if (!(id in current)) return current
        const next = { ...current }
        delete next[id]
        return next
      }
      return current[id] === line ? current : { ...current, [id]: line }
    })
  }, [])

  const terms = useMemo(
    () =>
      exposures.flatMap((exposure) => [
        exposure.asset,
        ...exposure.positions.map((risk) => risk.position.symbol),
      ]),
    [exposures]
  )
  const ai = useHomeAiBrief(terms)
  const writeBrief = useCallback(() => {
    // Only the cards that reported a line go in, so "D1" is the first of those.
    const described = datapoints.filter((datapoint) => summaries[datapoint.id])
    ai.start(
      buildBriefPrompt({
        exposures,
        openOrderCount: snapshot.openOrderCount,
        datapoints: described.map((datapoint) => summaries[datapoint.id]),
        ideas: ideas.map(describeIdea),
      }),
      ideas.map((idea) => idea.id),
      described.map((datapoint) => datapoint.id)
    )
  }, [ai, exposures, snapshot.openOrderCount, datapoints, summaries, ideas])

  return {
    ready,
    exposures,
    datapoints,
    ideas,
    tickerOf: (symbol) => (symbol ? tickers.get(symbol) : undefined),
    instrumentOf,
    reportSummary,
    ai,
    writeBrief,
    fullBriefOpen,
    setFullBriefOpen,
    hideKind: (kind) =>
      setHiddenKinds((current) => (current.includes(kind) ? current : [...current, kind])),
    hiddenCount: hiddenKinds.length,
    showAllKinds: () => setHiddenKinds([]),
  }
}
